import React, { useRef, useState } from "react";
import FooterSection from "./FooterSection";
import { useSector } from "./SectorContext";
import emailjs from "@emailjs/browser";

/* =========================================================
   FIELD LIMITS
   ========================================================= */

const FIELD_LIMITS = {
  name: 80,
  email: 254,
  phone: 25,
  budget: 100,
  message: 2000,
};

/* =========================================================
   VALIDATION PATTERNS
   ========================================================= */

/*
  Email:
  Simple practical email validation.
*/
const EMAIL_PATTERN =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/*
  Name:
  Allows:
  - Unicode letters
  - accents
  - spaces
  - apostrophes
  - commas
  - periods
  - hyphens
*/
const NAME_PATTERN =
  /^[\p{L}\p{M} .,'’\-]+$/u;

/*
  Phone:
  Allows:
  - digits
  - +
  - ()
  - spaces
  - periods
  - hyphens
*/
const PHONE_PATTERN =
  /^[0-9+() .\-]+$/;

/*
  Budget:
  Allows typical budget descriptions such as:

  $5,000
  $5,000 - $10,000
  Around 10k
  B$7,500
  USD 5,000
  5k / month
*/
const BUDGET_PATTERN =
  /^[\p{L}\p{N}\s$€£¥.,+\-–—/()]+$/u;

/*
  Reject invisible/control characters.

  Normal punctuation is deliberately allowed in the
  message field because punctuation itself is not
  an injection vulnerability.
*/
const CONTROL_CHAR_PATTERN =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

/* =========================================================
   NORMALIZATION
   ========================================================= */

const removeControlCharacters = (value = "") =>
  value.replace(CONTROL_CHAR_PATTERN, "");

const normalizeFormData = (data) => ({
  name: removeControlCharacters(data.name || "").trim(),

  email: removeControlCharacters(data.email || "")
    .trim()
    .toLowerCase(),

  phone: removeControlCharacters(data.phone || "").trim(),

  budget: removeControlCharacters(data.budget || "").trim(),

  message: removeControlCharacters(data.message || "").trim(),
});

/* =========================================================
   VALIDATION
   ========================================================= */

const validateForm = (data) => {
  const errors = {};

  const phoneDigits = data.phone.replace(/\D/g, "");

  /* ---------- Length limits ---------- */

  Object.entries(FIELD_LIMITS).forEach(([field, limit]) => {
    if (data[field].length > limit) {
      errors[field] =
        `Please use ${limit} characters or fewer.`;
    }
  });

  /* ---------- Name ---------- */

  if (!errors.name && data.name.length < 2) {
    errors.name =
      "Please enter at least 2 characters.";
  } else if (
    !errors.name &&
    !NAME_PATTERN.test(data.name)
  ) {
    errors.name =
      "Names may contain letters, spaces, apostrophes, hyphens, periods, and commas only.";
  }

  /* ---------- Email ---------- */

  if (
    !errors.email &&
    !EMAIL_PATTERN.test(data.email)
  ) {
    errors.email =
      "Please enter an email address in the format name@example.com.";
  }

  /* ---------- Phone ---------- */

  if (!errors.phone && data.phone) {
    if (!PHONE_PATTERN.test(data.phone)) {
      errors.phone =
        "Phone numbers may contain digits, spaces, +, -, parentheses, and periods only.";
    } else if (
      phoneDigits.length < 7 ||
      phoneDigits.length > 15
    ) {
      errors.phone =
        "Please enter a phone number containing between 7 and 15 digits.";
    }
  }

  /* ---------- Budget ---------- */

  if (
    !errors.budget &&
    data.budget &&
    !BUDGET_PATTERN.test(data.budget)
  ) {
    errors.budget =
      "Budget may contain letters, numbers, spaces, currency symbols, commas, periods, slashes, parentheses, and hyphens only.";
  }

  /* ---------- Message ---------- */

  if (!errors.message && data.message.length < 10) {
    errors.message =
      "Please provide a little more detail — at least 10 characters.";
  }

  return errors;
};

/* =========================================================
   COMPONENT
   ========================================================= */

function ContactPage() {
  const { setFormData, formData } = useSector();

  const form = useRef(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [submissionStatus, setSubmissionStatus] =
    useState("");

  /* =======================================================
     FIELD CHANGE
     ======================================================= */

  const handleChange = (event) => {
    const { name } = event.target;

    let value = removeControlCharacters(
      event.target.value,
    );

    /*
      Prevent invalid characters from entering the
      more tightly structured fields.
    */

    if (
      name === "name" &&
      value &&
      !NAME_PATTERN.test(value)
    ) {
      return;
    }

    if (
      name === "phone" &&
      value &&
      !PHONE_PATTERN.test(value)
    ) {
      return;
    }

    if (
      name === "budget" &&
      value &&
      !BUDGET_PATTERN.test(value)
    ) {
      return;
    }

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));

    /*
      Remove the field error as soon as the
      user begins correcting it.
    */

    if (errors[name]) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        [name]: "",
      }));
    }

    if (submissionStatus) {
      setSubmissionStatus("");
    }
  };

  /* =======================================================
     FIELD BLUR
     ======================================================= */

  const handleBlur = (event) => {
    const { name } = event.target;

    const normalizedData =
      normalizeFormData(formData);

    const fieldError =
      validateForm(normalizedData)[name] || "";

    setErrors((previousErrors) => ({
      ...previousErrors,
      [name]: fieldError,
    }));
  };

  /* =======================================================
     SUBMIT
     ======================================================= */

  const sendEmail = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    /*
      Honeypot bot check.
    */

    const honeypot =
      new FormData(form.current).get("website");

    if (honeypot) {
      setSubmissionStatus(
        "Thanks. Your message has been sent.",
      );

      return;
    }

    const normalizedData =
      normalizeFormData(formData);

    const validationErrors =
      validateForm(normalizedData);

    /*
      Stop submission if validation failed.
    */

    if (
      Object.keys(validationErrors).length > 0
    ) {
      setErrors(validationErrors);

      setSubmissionStatus(
        "Please review the highlighted fields and correct the errors shown.",
      );

      /*
        Move keyboard focus to the first invalid field.
      */

      const firstInvalidField =
        Object.keys(validationErrors)[0];

      const element =
        form.current?.elements[firstInvalidField];

      if (element) {
        element.focus();
      }

      return;
    }

    setErrors({});
    setIsSubmitting(true);
    setSubmissionStatus("");

    try {
      await emailjs.send(
        "service_nkwr7dy",
        "template_o5plu5u",
        normalizedData,
        {
          publicKey: "nIO_6i5IYGQX6DnfR",
        },
      );

      setFormData({
        name: "",
        email: "",
        phone: "",
        budget: "",
        message: "",
      });

      setSubmissionStatus(
        "Thanks. Your message has been sent.",
      );
    } catch {
      setSubmissionStatus(
        "Sorry. There was a problem sending your message. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-container">
      <div className="contact-header">
        <p>Lets Talk</p>
      </div>

      <form
        className="contact-form"
        ref={form}
        onSubmit={sendEmail}
        noValidate
      >
        {/* ================================
            NAME
            ================================ */}

        <label
          htmlFor="name"
          className="name-field"
        >
          <p>Name:</p>

          <input
            type="text"
            id="name"
            className="form-field"
            name="name"
            value={formData.name || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your name"
            autoComplete="name"
            minLength={2}
            maxLength={FIELD_LIMITS.name}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={
              errors.name
                ? "name-error"
                : undefined
            }
            required
          />

          {errors.name && (
            <span
              id="name-error"
              className="form-error"
            >
              {errors.name}
            </span>
          )}
        </label>

        {/* ================================
            EMAIL
            ================================ */}

        <label
          htmlFor="email"
          className="email-field"
        >
          <p>Email:</p>

          <input
            type="email"
            id="email"
            className="form-field"
            name="email"
            value={formData.email || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your email"
            autoComplete="email"
            inputMode="email"
            maxLength={FIELD_LIMITS.email}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={
              errors.email
                ? "email-error"
                : undefined
            }
            required
          />

          {errors.email && (
            <span
              id="email-error"
              className="form-error"
            >
              {errors.email}
            </span>
          )}
        </label>

        {/* ================================
            PHONE
            ================================ */}

        <label
          htmlFor="phone"
          className="phone-field"
        >
          <p>Phone:</p>

          <input
            type="tel"
            id="phone"
            className="form-field"
            name="phone"
            value={formData.phone || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your phone"
            autoComplete="tel"
            inputMode="tel"
            maxLength={FIELD_LIMITS.phone}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={
              errors.phone
                ? "phone-error"
                : undefined
            }
          />

          {errors.phone && (
            <span
              id="phone-error"
              className="form-error"
            >
              {errors.phone}
            </span>
          )}
        </label>

        {/* ================================
            BUDGET
            ================================ */}

        <label
          htmlFor="budget"
          className="budget-field"
        >
          <p>Project Budget:</p>

          <input
            id="budget"
            type="text"
            name="budget"
            className="form-field"
            value={formData.budget || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Do you have a budget?"
            maxLength={FIELD_LIMITS.budget}
            aria-invalid={Boolean(errors.budget)}
            aria-describedby={
              errors.budget
                ? "budget-error"
                : undefined
            }
          />

          {errors.budget && (
            <span
              id="budget-error"
              className="form-error"
            >
              {errors.budget}
            </span>
          )}
        </label>

        {/* ================================
            MESSAGE
            ================================ */}

        <label
          htmlFor="message"
          className="message-field"
        >
          <p>Message:</p>

          <textarea
            id="message"
            name="message"
            className="form-field"
            value={formData.message || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Tell me about your project"
            minLength={10}
            maxLength={FIELD_LIMITS.message}
            aria-invalid={Boolean(
              errors.message,
            )}
            aria-describedby={
              errors.message
                ? "message-error"
                : undefined
            }
            required
          />

          {errors.message && (
            <span
              id="message-error"
              className="form-error"
            >
              {errors.message}
            </span>
          )}
        </label>

        {/* ================================
            HONEYPOT
            ================================ */}

        <div
          className="contact-honeypot"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <label htmlFor="website">
            Website
          </label>

          <input
            id="website"
            name="website"
            type="text"
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* ================================
            SUBMIT
            ================================ */}

        <div className="submit-area">
          <button
            className="contact-button shiny-border"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Sending..."
              : "Send Message"}
          </button>

          <p
            className="form-status"
            aria-live="polite"
          >
            {submissionStatus}
          </p>
        </div>
      </form>

      <div className="cfooter">
        <FooterSection />
      </div>
    </div>
  );
}

export default ContactPage;